import React, { useState, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { dummyP2PDisputes } from "../../../data/dummyData";
import "../Home/Homepage.css";
import "./DisputeManagementPage.css";

const formatDate = (iso) => {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
  } catch {
    return iso || "N/A";
  }
};

const formatDateTime = (iso) => {
  try {
    const d = new Date(iso);
    return d.toLocaleString("en-IN", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch {
    return iso || "N/A";
  }
};

const buyerName = (row) => row.buyerInfo ? `${(row.buyerInfo.firstName || "").trim()} ${(row.buyerInfo.lastName || "").trim()}`.trim() || row.buyerInfo.emailId || "N/A" : "N/A";
const sellerName = (row) => row.sellerInfo ? `${(row.sellerInfo.firstName || "").trim()} ${(row.sellerInfo.lastName || "").trim()}`.trim() || row.sellerInfo.emailId || "N/A" : "N/A";
const resolvedByName = (row) => row.adminResolvedBy ? `${(row.adminResolvedBy.first_name || "").trim()} ${(row.adminResolvedBy.last_name || "").trim()}`.trim() || "Admin" : "N/A";

const DisputeManagementPage = () => {
  const navigate = useNavigate();
  const [disputes] = useState(dummyP2PDisputes);
  const [filters, setFilters] = useState({
    disputeBy: "",
    adminStatus: "",
    dateFrom: "",
    dateTo: "",
  });

  const updateFilter = (key, value) => setFilters((f) => ({ ...f, [key]: value }));

  const resetFilters = () => {
    setFilters({ disputeBy: "", adminStatus: "", dateFrom: "", dateTo: "" });
  };

  const handleViewDetails = (row) => {
    navigate("/dashboard/dispute_Details", { state: { orderId: row._id, disputeData: row } });
  };

  const filteredDisputes = useMemo(() => {
    return disputes.filter((d) => {
      if (filters.adminStatus) {
        const status = (d.adminDisputeStatus || (d.isDisputed ? "PENDING" : "")).toUpperCase();
        if (status !== filters.adminStatus.toUpperCase()) return false;
      }
      if (filters.disputeBy) {
        if ((d.disputeBy || "").toUpperCase() !== filters.disputeBy.toUpperCase()) return false;
      }
      if (filters.dateFrom && d.createdAt) {
        if (new Date(d.createdAt) < new Date(filters.dateFrom)) return false;
      }
      if (filters.dateTo && d.createdAt) {
        if (new Date(d.createdAt) > new Date(filters.dateTo + "T23:59:59")) return false;
      }
      return true;
    });
  }, [disputes, filters]);

  const counts = useMemo(() => ({
    total: disputes.length,
    pending: disputes.filter((d) => (d.adminDisputeStatus || (d.isDisputed ? "PENDING" : "")).toUpperCase() === "PENDING").length,
    resolved: disputes.filter((d) => (d.adminDisputeStatus || "").toUpperCase() === "RESOLVED").length,
  }), [disputes]);

  return (
    <main className="dashboard-main dashboard-subpage dispute-management-page">
      <section className="dashboard-hero dispute-hero">
        <div className="dashboard-hero__text">
          <p className="dashboard-hero__breadcrumb">
            <Link to="/dashboard/homepage">Dashboard</Link>
            <span style={{ margin: "0 0.35rem" }}>/</span>
            <span>Dispute Management</span>
          </p>
          <h1 className="dashboard-hero__title">P2P Dispute Management</h1>
          <p className="dashboard-hero__subtitle">Manage and resolve P2P trading disputes</p>
        </div>
        <div className="dispute-hero__icon">
          <i className="fas fa-wrench" />
        </div>
      </section>

      <div className="dashboard-body">
        <div className="dispute-stats">
          <div className="dispute-stat-card">
            <i className="fas fa-list dispute-stat-card__icon dispute-stat-card__icon--default" />
            <span className="dispute-stat-card__value">{counts.total}</span>
            <span className="dispute-stat-card__label">Total Disputes</span>
          </div>
          <div className="dispute-stat-card">
            <i className="fas fa-clock dispute-stat-card__icon dispute-stat-card__icon--pending" />
            <span className="dispute-stat-card__value">{counts.pending}</span>
            <span className="dispute-stat-card__label">Pending</span>
          </div>
          <div className="dispute-stat-card">
            <i className="fas fa-check-circle dispute-stat-card__icon dispute-stat-card__icon--resolved" />
            <span className="dispute-stat-card__value">{counts.resolved}</span>
            <span className="dispute-stat-card__label">Resolved</span>
          </div>
        </div>

        <section className="dispute-filters-section">
          <h2 className="dispute-filters__title"><i className="fas fa-filter" /> Filters</h2>
          <div className="dispute-filters-grid">
            <div className="dispute-filter-group">
              <label><i className="fas fa-user" /> Dispute By</label>
              <select value={filters.disputeBy} onChange={(e) => updateFilter("disputeBy", e.target.value)}>
                <option value="">All</option>
                <option value="BUYER">BUYER</option>
                <option value="SELLER">SELLER</option>
              </select>
            </div>
            <div className="dispute-filter-group">
              <label><i className="fas fa-info-circle" /> Admin Status</label>
              <select value={filters.adminStatus} onChange={(e) => updateFilter("adminStatus", e.target.value)}>
                <option value="">All</option>
                <option value="PENDING">Open (PENDING)</option>
                <option value="RESOLVED">Closed (RESOLVED)</option>
              </select>
            </div>
            <div className="dispute-filter-group">
              <label>Date From</label>
              <input type="date" value={filters.dateFrom} onChange={(e) => updateFilter("dateFrom", e.target.value)} />
            </div>
            <div className="dispute-filter-group">
              <label>Date To</label>
              <input type="date" value={filters.dateTo} onChange={(e) => updateFilter("dateTo", e.target.value)} />
            </div>
          </div>
          <div className="dispute-filters-actions">
            <button type="button" className="dispute-btn-reset" onClick={resetFilters}>
              <i className="fas fa-sync-alt" /> Reset Filters
            </button>
          </div>
        </section>

        <section className="dispute-list-section">
          <h2 className="dispute-list__title"><i className="fas fa-th-list" /> Disputes List ({filteredDisputes.length})</h2>
          <div className="dispute-table-wrap">
            <table className="dispute-table">
              <thead>
                <tr>
                  <th>SR NO.</th>
                  <th>ORDER ID</th>
                  <th>AD UID</th>
                  <th>SIDE</th>
                  <th>FIAT</th>
                  <th>QUOTE</th>
                  <th>FIAT AMOUNT</th>
                  <th>CRYPTO AMOUNT</th>
                  <th>BUYER</th>
                  <th>SELLER</th>
                  <th>DISPUTE BY</th>
                  <th>DISPUTE REASON</th>
                  <th>DISPUTE AT</th>
                  <th>ADMIN STATUS</th>
                  <th>ADMIN ACTION</th>
                  <th>RESOLVED BY</th>
                  <th>RESOLVED AT</th>
                  <th>CREATED AT</th>
                  <th>ACTIONS</th>
                </tr>
              </thead>
              <tbody>
                {filteredDisputes.map((row, index) => (
                  <tr key={row._id}>
                    <td>{index + 1}</td>
                    <td>{row._id?.substring(0, 12) || "N/A"}</td>
                    <td>{row.adInfo?.adUid ?? "N/A"}</td>
                    <td><span className={`dispute-side-badge dispute-side-badge--${(row.adInfo?.side || "").toLowerCase()}`}>{row.adInfo?.side ?? "N/A"}</span></td>
                    <td>{row.adInfo?.fiatCurrency ?? "N/A"}</td>
                    <td>{row.adInfo?.qouteCurrency ?? "N/A"}</td>
                    <td>{row.fiatAmount ?? "N/A"}</td>
                    <td>{row.cryptoAmount ?? "N/A"}</td>
                    <td>{buyerName(row)}</td>
                    <td>{sellerName(row)}</td>
                    <td><span className={`dispute-by-badge dispute-by-badge--${(row.disputeBy || "").toLowerCase()}`}>{row.disputeBy ?? "N/A"}</span></td>
                    <td className="dispute-table__reason">{row.disputeReason ?? "N/A"}</td>
                    <td>{row.disputeAt ? formatDate(row.disputeAt) : "N/A"}</td>
                    <td><span className={`dispute-admin-status dispute-admin-status--${(row.adminDisputeStatus || (row.isDisputed ? "PENDING" : "")).toLowerCase()}`}>{row.adminDisputeStatus || (row.isDisputed ? "PENDING" : "N/A")}</span></td>
                    <td>{row.adminDisputeAction ?? "N/A"}</td>
                    <td>{resolvedByName(row)}</td>
                    <td>{row.adminResolvedAt ? formatDateTime(row.adminResolvedAt) : "N/A"}</td>
                    <td>{row.createdAt ? formatDate(row.createdAt) : "N/A"}</td>
                    <td>
                      <button type="button" className="dispute-btn-view" onClick={() => handleViewDetails(row)}>
                        <i className="fas fa-eye" /> View Details
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      </div>
    </main>
  );
};

export default DisputeManagementPage;
